import { DEFAULT_MA_TELOP_TRACK_COLORS } from "../../../preview/ma/maTelopConfig";
import { canonicalMediaKey } from "../../../preview/ma/maTypes";
import type { NuendoArchive } from "./nuendoXml";
import type { MediaEntry } from "./projectFolder";

/**
 * Nuendo XML → RPP の移植プラン（RPP に書き出すトラック・アイテム・マーカー）。
 *
 * - ビデオトラック: Nuendo のビデオトラック 1 本 → REAPER トラック 1 本。
 *   動画の音声は鳴らさない（SOURCE VIDEO の AUDIO 0。絵は出したまま）
 * - マーカー: サイクルマーカー → リージョン、マーカー → マーカー。
 *   Nuendo のマーカートラックは同名のルーラーレーンに移す
 * - オーディオ: サイクルマーカー名と同名の音声が Media にあれば、
 *   マーカートラックと同名のトラックへリージョン開始位置で配置し、
 *   そのリージョンのレンダー対象トラック（Region Render Matrix）にする
 */

export type RppSourceType = "VIDEO" | "WAVE" | "MP3" | "FLAC" | "VORBIS";

export interface PlannedItem {
  name: string;
  /** タイムライン上の開始位置（秒） */
  position: number;
  length: number;
  /** ソース内のイン点（秒） */
  sourceOffset: number;
  /** RPP の FILE に書く相対パス */
  filePath: string;
  sourceType: RppSourceType;
  /** 動画の音声を鳴らさない */
  muteVideoAudio: boolean;
  /** Media に実体が見つかったか */
  found: boolean;
  /** 長さの出どころ（file = 音声ファイルの実長 / timeline = XML 上の長さ） */
  lengthSource: "file" | "timeline";
}

export interface PlannedTrack {
  /** プラン内の識別子（リージョンのレンダー対象の参照に使う） */
  key: string;
  name: string;
  kind: "video" | "audio";
  /** REAPER の色整数（null = 既定色） */
  color: number | null;
  items: PlannedItem[];
}

export interface PlannedLane {
  name: string;
  color: number;
}

export interface PlannedMarker {
  name: string;
  start: number;
  /** null = 通常マーカー */
  end: number | null;
  /** 置き先のルーラーレーン名（= Nuendo のマーカートラック名） */
  laneName: string;
  color: number;
  /** リージョンのレンダー対象トラック（PlannedTrack.key） */
  renderTrackKey: string | null;
  /** オーディオを置いたアイテム（プレビュー表示用） */
  audioItem: PlannedItem | null;
}

export interface MigrationPlan {
  /** 上から順に並べるトラック（ビデオ → オーディオ） */
  tracks: PlannedTrack[];
  lanes: PlannedLane[];
  markers: PlannedMarker[];
  warnings: string[];
}

/** RPP から参照する素材フォルダ（REAPER の既定の録音先と同じ） */
const DEFAULT_MEDIA_DIR = "Media";

/** サイクルマーカー名に続けて探す拡張子（先勝ち） */
const AUDIO_EXTENSIONS: ReadonlyArray<[string, RppSourceType]> = [
  ["wav", "WAVE"],
  ["aif", "WAVE"],
  ["aiff", "WAVE"],
  ["flac", "FLAC"],
  ["mp3", "MP3"],
  ["ogg", "VORBIS"],
];

/** REAPER の色整数のカスタム色フラグ */
const REAPER_CUSTOM_COLOR = 0x1000000;

/** MA の既定トラック色に無いレーン用の色（出現順に割り当て） */
const FALLBACK_LANE_COLORS = [
  "#4FC3F7",
  "#81C784",
  "#FFB74D",
  "#BA68C8",
  "#E57373",
  "#4DB6AC",
  "#F06292",
  "#AED581",
];

/** `#RRGGBB` → REAPER の色整数（カスタム色フラグ | COLORREF 0x00BBGGRR） */
export function hexToReaperColor(hex: string): number {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  return REAPER_CUSTOM_COLOR | (b << 16) | (g << 8) | r;
}

const createLaneColorPicker = () => {
  let fallbackIndex = 0;
  return (name: string): number => {
    const preset = DEFAULT_MA_TELOP_TRACK_COLORS.find(
      (c) => c.track.toLowerCase() === name.toLowerCase(),
    );
    if (preset) return hexToReaperColor(preset.color);
    const hex =
      FALLBACK_LANE_COLORS[fallbackIndex % FALLBACK_LANE_COLORS.length];
    fallbackIndex += 1;
    return hexToReaperColor(hex);
  };
};

/** 音声ファイルの長さ（秒）を測る。取得できない場合は null */
export type MeasureDuration = (entry: MediaEntry) => Promise<number | null>;

/** サイクルマーカー名と同名の音声を Media から探す（拡張子は AUDIO_EXTENSIONS の順） */
const findMarkerAudio = (
  media: Map<string, MediaEntry>,
  markerName: string,
): { entry: MediaEntry; sourceType: RppSourceType } | null => {
  for (const [ext, sourceType] of AUDIO_EXTENSIONS) {
    const entry = media.get(canonicalMediaKey(`${markerName}.${ext}`));
    if (entry) return { entry, sourceType };
  }
  return null;
};

/** 同じファイルを複数のサイクルマーカーで使っても 1 回だけ測る */
const cachedMeasure = (measure: MeasureDuration): MeasureDuration => {
  const cache = new Map<string, Promise<number | null>>();
  return (entry) => {
    let pending = cache.get(entry.relativePath);
    if (!pending) {
      pending = measure(entry).catch(() => null);
      cache.set(entry.relativePath, pending);
    }
    return pending;
  };
};

export async function buildMigrationPlan(
  archive: NuendoArchive,
  media: Map<string, MediaEntry>,
  measureDuration: MeasureDuration,
  /** ドロップされたフォルダ内の Media フォルダ名（無ければ null = 既定の Media） */
  mediaDirName: string | null = null,
): Promise<MigrationPlan> {
  const mediaDir = mediaDirName ?? DEFAULT_MEDIA_DIR;
  const warnings: string[] = [];
  const tracks: PlannedTrack[] = [];

  archive.videoTracks.forEach((videoTrack, trackIndex) => {
    const items: PlannedItem[] = [];
    videoTrack.events.forEach((event) => {
      if (!event.fileName) {
        warnings.push(
          `ビデオイベント「${event.name}」のファイル名が XML に無いためスキップしました`,
        );
        return;
      }
      const entry = media.get(canonicalMediaKey(event.fileName));
      if (!entry)
        warnings.push(
          `動画「${event.fileName}」が ${mediaDir} に見つかりません`,
        );
      items.push({
        name: event.name || event.fileName,
        position: event.start,
        length: event.length,
        sourceOffset: event.offset,
        filePath: entry?.relativePath ?? `${mediaDir}/${event.fileName}`,
        sourceType: "VIDEO",
        muteVideoAudio: true,
        found: !!entry,
        lengthSource: "timeline",
      });
    });
    if (items.length === 0) return;
    tracks.push({
      key: `video-${trackIndex}`,
      name: videoTrack.name,
      kind: "video",
      color: null,
      items,
    });
  });

  const pickLaneColor = createLaneColorPicker();
  const lanes: PlannedLane[] = [];
  const laneColorByName = new Map<string, number>();
  const audioTracks = new Map<string, PlannedTrack>();
  const markers: PlannedMarker[] = [];
  const measure = cachedMeasure(measureDuration);

  for (const markerTrack of archive.markerTracks) {
    const laneName = markerTrack.name;
    let laneColor = laneColorByName.get(laneName);
    if (laneColor === undefined) {
      laneColor = pickLaneColor(laneName);
      laneColorByName.set(laneName, laneColor);
      lanes.push({ name: laneName, color: laneColor });
    }

    for (const marker of markerTrack.markers) {
      const isCycle = marker.length > 0;
      const planned: PlannedMarker = {
        name: marker.name,
        start: marker.start,
        end: isCycle ? marker.start + marker.length : null,
        laneName,
        color: laneColor,
        renderTrackKey: null,
        audioItem: null,
      };
      markers.push(planned);
      if (!isCycle || !marker.name) continue;

      const audio = findMarkerAudio(media, marker.name);
      if (!audio) continue;
      const { entry, sourceType } = audio;

      const duration = await measure(entry);
      if (duration === null) {
        warnings.push(
          `「${entry.relativePath}」の長さを読み取れなかったため、サイクルマーカーの長さで配置しました`,
        );
      }

      let track = audioTracks.get(laneName);
      if (!track) {
        track = {
          key: `audio-${laneName}`,
          name: laneName,
          kind: "audio",
          color: laneColor,
          items: [],
        };
        audioTracks.set(laneName, track);
      }
      const item: PlannedItem = {
        name: entry.file.name,
        position: marker.start,
        length: duration ?? marker.length,
        sourceOffset: 0,
        filePath: entry.relativePath,
        sourceType,
        muteVideoAudio: false,
        found: true,
        lengthSource: duration === null ? "timeline" : "file",
      };
      track.items.push(item);
      planned.renderTrackKey = track.key;
      planned.audioItem = item;
    }
  }

  audioTracks.forEach((track) => {
    track.items.sort((a, b) => a.position - b.position);
    tracks.push(track);
  });
  markers.sort((a, b) => a.start - b.start);

  if (tracks.length === 0 && markers.length === 0) {
    warnings.push("XML にビデオトラック・マーカートラックが見つかりません");
  }

  // 同じ音声を複数のサイクルマーカーで使うと同じ警告が並ぶため畳む
  return { tracks, lanes, markers, warnings: [...new Set(warnings)] };
}
